/**
 * Evidence Resolver — Phase 3 of native GL.
 *
 * Given a posted journal entry's sourceRef, walk the canonical operational
 * data and return ordered, human-readable evidence items. Pure function;
 * no side effects.
 *
 * This is the moat. Every dollar in the GL traces back to operational proof:
 *   • Pay runs → wrap record → installer + outcome + completion% + service checklist
 *   • Job complete → wrap + customer-informed timestamp + settlement status
 *   • AR invoices → customer + contract value + payment plan
 *   • Cash receipts → method + timestamp + invoice link
 *
 * No accounting system on earth links debits and credits back to install
 * photos, GPS check-ins, and signed proof. That's the wedge.
 */
import type { Job } from "@/data/types";
import type { PayRunRecord } from "@/data/finance/payRun/recordPayRun";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import type { JournalEntry } from "./glTypes";

export type EvidenceKind =
  | "customer"
  | "contract"
  | "invoice"
  | "payment"
  | "installer"
  | "wrap"
  | "qa"
  | "service_checklist"
  | "field_add"
  | "callback"
  | "pay_ledger"
  | "settlement"
  | "address"
  | "schedule"
  | "note";

export interface EvidenceItem {
  kind: EvidenceKind;
  label: string;
  detail?: string;
  timestamp?: string;
  /** Optional link target inside the app, e.g. "/jobs/123" or "/finance/pay-runs". */
  link?: string;
  /** Optional muted tone (downgrades the item visually). */
  tone?: "neutral" | "positive" | "warn";
}

export interface EvidenceContext {
  jobs: Job[];
  payRunRecords: PayRunRecord[];
  installerWrapRecords: InstallerWrapRecord[];
}

export function resolveEvidence(
  entry: JournalEntry,
  ctx: EvidenceContext,
): EvidenceItem[] {
  if (!entry.sourceRef) return [];
  switch (entry.sourceRef.kind) {
    case "invoice":   return resolveInvoiceEvidence(entry, ctx);
    case "payment":   return resolvePaymentEvidence(entry, ctx);
    case "pay_run":   return resolvePayRunEvidence(entry, ctx);
    case "job_complete": return resolveJobCompleteEvidence(entry, ctx);
    case "reversal_of":  return resolveReversalEvidence(entry, ctx);
    default: return [];
  }
}

// ── Per-kind resolvers ───────────────────────────────────────────────────────

function findJobFromEntry(entry: JournalEntry, ctx: EvidenceContext): Job | undefined {
  const jobId = entry.lines.find(l => l.jobId)?.jobId;
  if (!jobId) return undefined;
  return ctx.jobs.find(j => j.id === jobId);
}

function resolveInvoiceEvidence(entry: JournalEntry, ctx: EvidenceContext): EvidenceItem[] {
  const invoiceId = entry.sourceRef!.id;
  const job = ctx.jobs.find(j => j.financials?.invoices?.some(i => i.id === invoiceId));
  if (!job) return [];
  const inv = job.financials!.invoices!.find(i => i.id === invoiceId)!;
  const items: EvidenceItem[] = [
    { kind: "customer", label: "Customer", detail: job.customerName, link: `/customers/${encodeURIComponent(job.customerName)}` },
    { kind: "address", label: "Job site", detail: formatAddress(job), link: `/jobs/${job.id}` },
    { kind: "invoice", label: `Invoice ${inv.id}`,
      detail: `${inv.type === "final" ? "Final" : inv.type === "progress" ? "Progress" : "Deposit"} · $${inv.amount.toLocaleString()} · status ${inv.status}`,
      timestamp: inv.issuedAt, link: `/jobs/${job.id}` },
    { kind: "contract", label: "Contract value",
      detail: `$${(job.financials?.contractValue ?? 0).toLocaleString()} · ${job.projectType}`,
      link: `/jobs/${job.id}` },
  ];
  if (inv.dueDate) items.push({ kind: "schedule", label: "Due date", detail: inv.dueDate });
  return items;
}

function resolvePaymentEvidence(entry: JournalEntry, ctx: EvidenceContext): EvidenceItem[] {
  const paymentId = entry.sourceRef!.id;
  let foundJob: Job | undefined;
  let payment: any;
  for (const j of ctx.jobs) {
    const p = j.financials?.payments?.find(p => p.id === paymentId);
    if (p) { foundJob = j; payment = p; break; }
  }
  if (!foundJob || !payment) return [];
  const inv = foundJob.financials?.invoices?.find(i => i.id === payment.invoiceId);
  const items: EvidenceItem[] = [
    { kind: "customer", label: "Customer", detail: foundJob.customerName, link: `/customers/${encodeURIComponent(foundJob.customerName)}` },
    { kind: "payment", label: `Payment ${payment.id}`,
      detail: `$${payment.amount.toLocaleString()} · ${payment.method}`,
      timestamp: payment.timestamp, tone: "positive" },
    { kind: "invoice", label: `Against invoice ${payment.invoiceId}`,
      detail: inv ? `${inv.type} · status ${inv.status}` : "Linked invoice",
      link: `/jobs/${foundJob.id}` },
  ];
  if (payment.method === "credit_card") {
    items.push({ kind: "note", label: "Reconciliation", detail: "Sitting in Unreconciled Cash (1090) until bank feed match.", tone: "warn" });
  }
  return items;
}

function resolvePayRunEvidence(entry: JournalEntry, ctx: EvidenceContext): EvidenceItem[] {
  const runId = entry.sourceRef!.id;
  const rec = ctx.payRunRecords.find(r => r.id === runId);
  if (!rec) return [];
  const job = ctx.jobs.find(j => j.id === rec.jobId);
  const wrap = rec.wrapRecordId ? ctx.installerWrapRecords.find(w => w.id === rec.wrapRecordId) : undefined;

  const items: EvidenceItem[] = [];
  if (job) {
    items.push({ kind: "customer", label: "Customer", detail: job.customerName, link: `/customers/${encodeURIComponent(job.customerName)}` });
    items.push({ kind: "address", label: "Job site", detail: formatAddress(job), link: `/jobs/${job.id}` });
  }
  items.push({
    kind: "pay_ledger",
    label: `Pay run ${rec.id}`,
    detail: `Net $${rec.netReleased.toLocaleString()} · released by ${rec.releasedBy} · ${rec.partyKind === "internal_crew" ? "Internal crew" : "Subcontractor"}`,
    timestamp: rec.releasedAt,
    link: "/finance/pay-runs",
  });
  if (wrap) {
    items.push({
      kind: "wrap",
      label: "Installer wrap",
      detail: `Outcome: ${wrap.outcome.replace(/_/g, " ")} · ${wrap.completionPercent}% complete${wrap.anotherDayNeeded ? " · returning" : ""}`,
      timestamp: wrap.timestamp,
      tone: wrap.outcome === "completed_today" ? "positive" : "neutral",
    });
    if (wrap.customerComm?.customerInformed) {
      items.push({
        kind: "note",
        label: "Customer informed",
        detail: wrap.customerComm.communicationSummary || undefined,
        timestamp: wrap.timestamp,
        tone: "positive",
      });
    }
    if (wrap.serviceChecklist) {
      const sc = wrap.serviceChecklist as Record<string, any>;
      const checks = Object.keys(sc).filter(k => sc[k] === true).length;
      items.push({
        kind: "service_checklist",
        label: "Service-call prevention checklist",
        detail: `${checks} checks completed`,
        tone: "positive",
      });
    }
    if (wrap.settlementStatus) {
      items.push({
        kind: "settlement",
        label: "Settlement status",
        detail: wrap.settlementStatus.replace(/_/g, " "),
      });
    }
  }
  // Field-add / callback line refs from the pay-run ledger
  const fieldAdds = rec.ledgerLines.filter(l => l.category === "field_add");
  if (fieldAdds.length > 0) {
    items.push({
      kind: "field_add",
      label: `${fieldAdds.length} field add${fieldAdds.length > 1 ? "s" : ""}`,
      detail: fieldAdds.map(l => `${l.memo} ($${(l.amountCents / 100).toLocaleString()})`).join(" · "),
    });
  }
  const callbacks = rec.ledgerLines.filter(l => l.memo?.toLowerCase().includes("callback"));
  if (callbacks.length > 0) {
    items.push({
      kind: "callback",
      label: `${callbacks.length} callback deduction${callbacks.length > 1 ? "s" : ""}`,
      detail: callbacks.map(l => l.memo).join(" · "),
      tone: "warn",
    });
  }
  return items;
}

function resolveJobCompleteEvidence(entry: JournalEntry, ctx: EvidenceContext): EvidenceItem[] {
  const jobId = entry.sourceRef!.id;
  const job = ctx.jobs.find(j => j.id === jobId);
  if (!job) return [];
  const wraps = ctx.installerWrapRecords.filter(w => w.jobId === jobId).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const finalWrap = wraps[wraps.length - 1];

  const items: EvidenceItem[] = [
    { kind: "customer", label: "Customer", detail: job.customerName, link: `/customers/${encodeURIComponent(job.customerName)}` },
    { kind: "address", label: "Job site", detail: formatAddress(job), link: `/jobs/${job.id}` },
    { kind: "contract", label: "Contract value", detail: `$${(job.financials?.contractValue ?? 0).toLocaleString()} · ${job.projectType}`, link: `/jobs/${job.id}` },
    { kind: "schedule", label: "Install date", detail: job.scheduledInstallDate },
  ];
  if (finalWrap) {
    items.push({
      kind: "wrap", label: "Final installer wrap",
      detail: `${finalWrap.outcome.replace(/_/g, " ")} · ${finalWrap.completionPercent}% · installer ${finalWrap.installerId}`,
      timestamp: finalWrap.timestamp, tone: "positive",
    });
    if (finalWrap.customerComm?.customerInformed) {
      items.push({
        kind: "note", label: "Customer signed off",
        detail: finalWrap.customerComm.communicationSummary || undefined,
        timestamp: finalWrap.timestamp, tone: "positive",
      });
    }
    if (finalWrap.serviceChecklist) {
      const sc = finalWrap.serviceChecklist as Record<string, any>;
      const checks = Object.keys(sc).filter(k => sc[k] === true).length;
      items.push({ kind: "service_checklist", label: "Service-call prevention", detail: `${checks} checks completed`, tone: "positive" });
    }
  }
  const invoices = job.financials?.invoices ?? [];
  const paid = invoices.filter(i => i.status === "paid");
  if (invoices.length > 0) {
    items.push({
      kind: "invoice", label: "Invoice ledger",
      detail: `${paid.length} of ${invoices.length} paid · $${invoices.reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()} billed`,
      link: `/jobs/${job.id}`,
      tone: paid.length === invoices.length ? "positive" : "neutral",
    });
  }
  return items;
}

function resolveReversalEvidence(entry: JournalEntry, _ctx: EvidenceContext): EvidenceItem[] {
  return [{
    kind: "note",
    label: "Reversing entry",
    detail: `Backs out original posting ${entry.sourceRef!.id}.`,
    tone: "warn",
  }];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatAddress(job: Job): string {
  const a = job.address;
  if (!a) return "—";
  return [a.street, a.city, a.state, a.zip].filter(Boolean).join(", ");
}
